import { Widget as WidgetType } from "../types/dashboard";
import { useDashboard } from "../hooks/useDashboard";

import { ParameterData } from "../../../types/device";
import { FloorPlanUpload } from "../../../components/widgets/map/FloorPlanUpload";
import ParameterTableWidget from "../../../components/widgets/table/parameter";
import MqttDataDisplay from "../../../components/MqttDataDisplay";
import GraphWidget from "../../../widgets/graph/GraphWidget";

import ConfigurationWidget from "../../../widgets/configuration/ConfigurationWidget";
import ReportWidget from "../../../widgets/report/ReportWidget";
import { useDeviceStore } from "../../../store/useDeviceStore";
import { useState, useRef, useEffect } from "react";
import useMqttStore from "../../../store/useMqttStore";

interface WidgetProps {
  widget: WidgetType;
  onRemove: (id: string, deviceId: string) => void;
  isDragging?: boolean;
  dragHandleProps?: any;
  isEditMode?: boolean;
}

const colorClasses = {
  primary: {
    bg: "bg-blue-50 dark:bg-blue-900/20",
    border: "border-blue-200 dark:border-blue-800",
    text: "text-blue-900 dark:text-blue-100",
    accent: "text-blue-600 dark:text-blue-400",
    icon: "text-blue-500 dark:text-blue-400",
  },
  success: {
    bg: "bg-green-50 dark:bg-green-900/20",
    border: "border-green-200 dark:border-green-800",
    text: "text-green-900 dark:text-green-100",
    accent: "text-green-600 dark:text-green-400",
    icon: "text-green-500 dark:text-green-400",
  },
  warning: {
    bg: "bg-yellow-50 dark:bg-yellow-900/20",
    border: "border-yellow-200 dark:border-yellow-800",
    text: "text-yellow-900 dark:text-yellow-100",
    accent: "text-yellow-600 dark:text-yellow-400",
    icon: "text-yellow-500 dark:text-yellow-400",
  },
  danger: {
    bg: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-200 dark:border-red-800",
    text: "text-red-900 dark:text-red-100",
    accent: "text-red-600 dark:text-red-400",
    icon: "text-red-500 dark:text-red-400",
  },
  info: {
    bg: "bg-indigo-50 dark:bg-indigo-900/20",
    border: "border-indigo-200 dark:border-indigo-800",
    text: "text-indigo-900 dark:text-indigo-100",
    accent: "text-indigo-600 dark:text-indigo-400",
    icon: "text-indigo-500 dark:text-indigo-400",
  },
};

const sizeClasses = {
  small: "min-h-32",
  medium: "min-h-40",
  large: "min-h-48",
  full: "min-h-56",
};

export default function Widget({
  widget,
  onRemove,
  isDragging,
  dragHandleProps,
  isEditMode,
}: WidgetProps) {
  const colors = colorClasses[widget.color];
  const sizeClass = sizeClasses[widget.size];
  const { selectedDeviceId } = useDeviceStore();
  const { updateWidget } = useDashboard();
  const { isConnected, deviceDataMap } = useMqttStore();

  // Inline editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(widget.title);
  const [tempSubtitle, setTempSubtitle] = useState(widget.subtitle);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);

  // Update temp values when widget changes
  useEffect(() => {
    setTempTitle(widget.title);
    setTempSubtitle(widget.subtitle);
  }, [widget.title, widget.subtitle]);

  // Auto-focus inputs when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (isEditingSubtitle && subtitleInputRef.current) {
      subtitleInputRef.current.focus();
      subtitleInputRef.current.select();
    }
  }, [isEditingSubtitle]);

  // Handle saving changes
  const saveTitle = () => {
    if (tempTitle.trim() !== widget.title) {
      updateWidget(widget.id, { title: tempTitle.trim() }, selectedDeviceId || "default");
    }
    setIsEditingTitle(false);
  };

  const saveSubtitle = () => {
    if (tempSubtitle.trim() !== widget.subtitle) {
      updateWidget(widget.id, { subtitle: tempSubtitle.trim() }, selectedDeviceId || "default");
    }
    setIsEditingSubtitle(false);
  };

  // Handle canceling changes
  const cancelTitleEdit = () => {
    setTempTitle(widget.title);
    setIsEditingTitle(false);
  };

  const cancelSubtitleEdit = () => {
    setTempSubtitle(widget.subtitle);
    setIsEditingSubtitle(false);
  };

  // Keyboard handlers
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelTitleEdit();
    }
  };

  const handleSubtitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveSubtitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelSubtitleEdit();
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditingTitle &&
        titleInputRef.current &&
        !titleInputRef.current.contains(event.target as Node)
      ) {
        saveTitle();
      }
      if (
        isEditingSubtitle &&
        subtitleInputRef.current &&
        !subtitleInputRef.current.contains(event.target as Node)
      ) {
        saveSubtitle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditingTitle, isEditingSubtitle, tempTitle, tempSubtitle, widget.title, widget.subtitle]);

  return (
    <div className="flex-1 w-full min-h-0">
      {widget.type === "table" ? (
        <ParameterTableWidget isEditMode={isEditMode} />
      ) : widget.type === "map" ? (
        <FloorPlanUpload isEditMode={isEditMode} />
      ) :
      widget.type === "grid" ? (
        <MqttDataDisplay
          liveData={selectedDeviceId ? deviceDataMap[selectedDeviceId]?.data : null}
          isConnected={isConnected}
          deviceId={selectedDeviceId}
          title={widget.title}
          subtitle={widget.subtitle}
          onDetailClick={(parameter: ParameterData) => {
            console.log("Parameter detail clicked:", parameter);
          }}
        />
      ) : widget.type === "graph" ? (
        <GraphWidget isEditMode={isEditMode} />
      ) : widget.type === "configuration" ? (
        <ConfigurationWidget isEditMode={isEditMode} />
      ) : widget.type === "report" ? (
        <ReportWidget isEditMode={isEditMode} />
      ) : (
        <div className="flex flex-col justify-center h-full"></div>
      )}
    </div>
  );
}
